/**
 * Runtime performance monitoring for virtual background
 * Validates device capabilities against actual performance
 * Accounts for video conference overhead (HD streams, audio, participants)
 */

import logger from '../../virtual-background/logger';

/**
 * Performance monitoring and validation
 * Tests actual frame processing performance to validate device capabilities
 */
export class PerformanceMonitor {
    /**
     * Initialize performance monitor
     */
    constructor() {
        this._frameTimings = [];
        this._maxFrameTimings = 60; // Keep last 60 frame timings
        this._videoConferenceOverhead = 0; // Detected overhead from video conference
        this._monitoringActive = false;
        this._testFrameCount = 0;
    }

    /**
     * Start monitoring frame processing times
     * Call this after virtual background starts processing
     *
     * @returns {void}
     */
    startMonitoring() {
        this._monitoringActive = true;
        this._frameTimings = [];
        this._testFrameCount = 0;
        logger.debug('[PerformanceMonitor] Monitoring started');
    }

    /**
     * Stop monitoring
     *
     * @returns {void}
     */
    stopMonitoring() {
        this._monitoringActive = false;
        logger.debug('[PerformanceMonitor] Monitoring stopped');
    }

    /**
     * Record frame processing time
     * Call this after each frame is processed
     *
     * @param {number} frameTimeMs - Time to process frame in milliseconds
     * @returns {void}
     */
    recordFrameTime(frameTimeMs) {
        if (!this._monitoringActive) {
            return;
        }

        this._frameTimings.push(frameTimeMs);
        this._testFrameCount++;

        // Keep only last N timings to avoid memory bloat
        if (this._frameTimings.length > this._maxFrameTimings) {
            this._frameTimings.shift();
        }
    }

    /**
     * Get average frame time from recent frames
     *
     * @returns {number} Average frame time in milliseconds
     */
    getAverageFrameTime() {
        if (this._frameTimings.length === 0) {
            return 0;
        }

        const sum = this._frameTimings.reduce((a, b) => a + b, 0);

        return sum / this._frameTimings.length;
    }

    /**
     * Get 95th percentile frame time (for detecting occasional spikes)
     *
     * @returns {number} 95th percentile frame time
     */
    getPercentile95FrameTime() {
        if (this._frameTimings.length === 0) {
            return 0;
        }

        const sorted = [ ...this._frameTimings ].sort((a, b) => a - b);
        const index = Math.ceil(sorted.length * 0.95) - 1;

        return sorted[Math.max(0, index)];
    }

    /**
     * Get max frame time from recent frames
     *
     * @returns {number} Maximum frame time
     */
    getMaxFrameTime() {
        if (this._frameTimings.length === 0) {
            return 0;
        }

        return Math.max(...this._frameTimings);
    }

    /**
     * Check if device can handle target FPS
     * Returns true if average frame time is within budget
     *
     * @param {number} targetFps - Target frames per second
     * @param {number} marginPercent - Safety margin (default 20%)
     * @returns {boolean} True if device can handle FPS
     */
    canHandle(targetFps, marginPercent = 20) {
        const frameBudgetMs = 1000 / targetFps;
        const budgetWithMargin = frameBudgetMs * ((100 - marginPercent) / 100);
        const averageFrameTime = this.getAverageFrameTime();

        return averageFrameTime < budgetWithMargin;
    }

    /**
     * Detect video conference overhead (CPU/GPU usage from streams, audio, etc)
     * Should be called before starting virtual background processing
     * Measures baseline CPU usage when only video conference is active
     *
     * @returns {Promise<number>} Estimated video conference overhead percentage
     */
    async detectVideoConferenceOverhead() {
        try {
            // Measure baseline CPU usage for 500ms
            const baselineSamples = [];
            const sampleIntervalMs = 50;
            const durationMs = 500;
            const sampleCount = durationMs / sampleIntervalMs;

            for (let i = 0; i < sampleCount; i++) {
                const startTime = performance.now();

                // Simulate small workload to measure CPU responsiveness
                // Calculate something CPU-intensive to detect interference
                let cpuBoundWork = 0;

                for (let j = 0; j < 1000000; j++) {
                    cpuBoundWork += Math.sqrt(j);
                }

                // Use result to prevent optimization
                void cpuBoundWork;

                const elapsedMs = performance.now() - startTime;

                baselineSamples.push(elapsedMs);

                // Wait before next sample
                await new Promise(resolve => setTimeout(resolve, sampleIntervalMs));
            }

            // Analyze samples - higher variance = more CPU interference
            const avgBaseline = baselineSamples.reduce((a, b) => a + b, 0) / baselineSamples.length;
            const variance = baselineSamples.reduce((sum, val) => sum + Math.pow(val - avgBaseline, 2), 0)
                / baselineSamples.length;
            const stdDev = Math.sqrt(variance);

            // Estimate overhead based on variance
            // High variance = interference from video/audio processing
            const estimatedOverhead = Math.min(50, stdDev * 10); // 0-50% estimated

            this._videoConferenceOverhead = estimatedOverhead;
            logger.debug(`[PerformanceMonitor] Video conference overhead: ${estimatedOverhead.toFixed(1)}%`);

            return estimatedOverhead;
        } catch (e) {
            logger.error('[PerformanceMonitor] Failed to detect video conference overhead', e);

            return 0;
        }
    }

    /**
     * Get estimated video conference overhead
     *
     * @returns {number} Percentage of CPU/GPU used by video conference (0-50)
     */
    getVideoConferenceOverhead() {
        return this._videoConferenceOverhead;
    }

    /**
     * Get performance summary for logging/debugging
     *
     * @returns {Object} Performance metrics summary
     */
    getPerformanceSummary() {
        return {
            averageFrameTime: this.getAverageFrameTime(),
            maxFrameTime: this.getMaxFrameTime(),
            percentile95: this.getPercentile95FrameTime(),
            frameCount: this._testFrameCount,
            videoConferenceOverhead: this._videoConferenceOverhead,
            samplesCollected: this._frameTimings.length
        };
    }

    /**
     * Format performance summary as readable string
     *
     * @returns {string} Formatted performance metrics
     */
    formatPerformanceSummary() {
        const summary = this.getPerformanceSummary();

        return `Frame Time: avg=${summary.averageFrameTime.toFixed(1)}ms, `
            + `p95=${summary.percentile95.toFixed(1)}ms, `
            + `max=${summary.maxFrameTime.toFixed(1)}ms | `
            + `Video Conf Overhead: ${summary.videoConferenceOverhead.toFixed(1)}% | `
            + `Samples: ${summary.samplesCollected}`;
    }
}

/**
 * Validate device capabilities with actual performance testing
 * Runs benchmarks to verify device can handle virtual background
 *
 * @param {Object} deviceCapabilities - Device capability info from deviceCapabilities.js
 * @returns {Promise<Object>} Validation result with recommended tier
 */
export async function validateDeviceCapabilities(deviceCapabilities) {
    const monitor = new PerformanceMonitor();
    const validation = {
        detectedCapability: deviceCapabilities.capability,
        recommendedCapability: deviceCapabilities.capability,
        isValid: true,
        issues: [],
        warnings: [],
        performanceProfile: null
    };

    try {
        // Detect video conference overhead
        logger.debug('[PerformanceMonitor] Detecting video conference overhead...');
        await monitor.detectVideoConferenceOverhead();

        const overhead = monitor.getVideoConferenceOverhead();

        // Warn if significant overhead detected
        if (overhead > 30) {
            validation.warnings.push(
                `High video conference overhead detected (${overhead.toFixed(1)}%). `
                + 'Virtual background may cause jitter with multiple participants.'
            );

            // Downgrade tier if high overhead
            if (deviceCapabilities.capability === 'high') {
                validation.recommendedCapability = 'medium';
                validation.warnings.push('Downgrading from HIGH to MEDIUM tier due to video conference load.');
            } else if (deviceCapabilities.capability === 'medium') {
                validation.recommendedCapability = 'low';
                validation.warnings.push('Downgrading from MEDIUM to LOW tier due to video conference load.');
            }
        }

        // Check for insufficient device memory
        if (typeof deviceCapabilities.cpuCores === 'number' && deviceCapabilities.cpuCores < 2) {
            validation.issues.push('Device has insufficient CPU cores (<2).');
            validation.isValid = false;
        }

        if (typeof deviceCapabilities.deviceMemory === 'number' && deviceCapabilities.deviceMemory < 2) {
            validation.issues.push('Device has insufficient RAM (<2GB).');
            validation.isValid = false;
        }

        // Check for WebGL unavailability
        if (!deviceCapabilities.hasGpu && deviceCapabilities.capability === 'high') {
            validation.warnings.push('No GPU detected. Falling back to CPU processing.');
            validation.recommendedCapability = 'medium';
        }

        validation.performanceProfile = {
            videoConferenceOverhead: overhead,
            detectionMethod: 'runtime-sampling',
            confidence: 'medium'
        };

        logger.info('[PerformanceMonitor] Validation complete: '
            + `detected=${validation.detectedCapability}, `
            + `recommended=${validation.recommendedCapability}, `
            + `valid=${validation.isValid}`);

    } catch (e) {
        logger.error('[PerformanceMonitor] Validation error', e);
        validation.isValid = false;
        validation.issues.push(`Validation failed: ${e.message}`);
    }

    return validation;
}

/**
 * Simulate virtual background processing to measure actual capability
 * Runs a short benchmark to see if device can handle target FPS
 *
 * @param {number} targetFps - Target FPS to test
 * @param {number} durationMs - How long to run benchmark (default 2000ms)
 * @returns {Promise<Object>} Benchmark results
 */
export async function benchmarkDeviceCapability(targetFps = 30, durationMs = 2000) {
    const monitor = new PerformanceMonitor();

    monitor.startMonitoring();

    const frameBudgetMs = 1000 / targetFps;
    const results = {
        targetFps,
        frameBudgetMs,
        canAchieveFps: false,
        benchmarkDuration: durationMs,
        performance: null,
        recommendation: 'medium'
    };

    try {
        // Simulate frame processing for duration
        const startTime = performance.now();

        while (performance.now() - startTime < durationMs) {
            const frameStart = performance.now();

            // Simulate some work (canvas operations, etc)
            const canvas = document.createElement('canvas');

            canvas.width = 256;
            canvas.height = 144;
            const ctx = canvas.getContext('2d');

            // Draw and filter operations (like virtual background)
            ctx.fillStyle = '#000000';
            ctx.fillRect(0, 0, 256, 144);
            ctx.filter = 'brightness(1.2) contrast(1.1) saturate(1.0)';
            ctx.drawImage(canvas, 0, 0);

            const frameTimeMs = performance.now() - frameStart;

            monitor.recordFrameTime(frameTimeMs);

            // Yield to other tasks
            await new Promise(resolve => setTimeout(resolve, 0));
        }

        monitor.stopMonitoring();

        // Analyze results
        results.performance = monitor.getPerformanceSummary();
        results.canAchieveFps = monitor.canHandle(targetFps, 20);

        // Recommend capability based on results
        if (results.canAchieveFps) {
            results.recommendation = 'high';
        } else if (monitor.canHandle(Math.floor(targetFps / 2), 20)) {
            results.recommendation = 'medium';
        } else {
            results.recommendation = 'low';
        }

        logger.info('[PerformanceMonitor] Benchmark complete: '
            + `targetFps=${targetFps}, `
            + `achievable=${results.canAchieveFps}, `
            + `avgFrameTime=${results.performance.averageFrameTime.toFixed(1)}ms, `
            + `recommended=${results.recommendation}`);

    } catch (e) {
        logger.error('[PerformanceMonitor] Benchmark error', e);
        results.recommendation = 'low';
    }

    return results;
}

export default PerformanceMonitor;
